import { useMutation } from "@tanstack/react-query";

import { useAuthHeaders } from "../auth/apiClient";

/** Florida TCPA window: 08:00 inclusive – 20:00 exclusive America/New_York. */
export function isFloridaSmsSendWindow(now = new Date()): boolean {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    weekday: "short",
    hour: "numeric",
    hour12: false,
  }).formatToParts(now);
  const weekday = parts.find((p) => p.type === "weekday")?.value || "";
  const hourRaw = parts.find((p) => p.type === "hour")?.value || "0";
  const hour = Number(hourRaw === "24" ? "0" : hourRaw);
  if (weekday === "Sun") return false;
  return hour >= 8 && hour < 20;
}

export function useSendSmsThread(threadKey: string | null) {
  const headers = useAuthHeaders(true);
  return useMutation({
    mutationFn: async (payload: { body: string; quoteId?: string | null; force?: boolean }) => {
      if (!threadKey) throw new Error("No SMS thread selected");
      const body = String(payload.body || "").trim();
      if (!body) throw new Error("Message body is required");
      if (!payload.force && !isFloridaSmsSendWindow()) {
        throw new Error("SMS can only be sent 8:00–20:00 Florida time (no Sunday).");
      }

      // Prefer quote-action send when a quote is linked (full audit + opt-out guards).
      if (payload.quoteId) {
        const forceSuffix = payload.force ? "?force=true" : "";
        const res = await fetch(`/api/cockpit/quotes/${encodeURIComponent(payload.quoteId)}/actions/send${forceSuffix}`, {
          method: "POST",
          headers,
          body: JSON.stringify({
            actionType: "manual_reply",
            channel: "sms",
            subject: "",
            body,
            confirmToken: "YES-QUOTE-ACTION-SEND",
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.reason || data.detail || data.error || "SMS send failed");
        }
        return data as { ok: true; messageId?: string | null };
      }

      const res = await fetch(`/api/cockpit/sms/threads/${encodeURIComponent(threadKey)}/send`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          body,
          confirmToken: "YES-SMS-THREAD-SEND",
          force: Boolean(payload.force),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.reason || data.detail || data.error || "SMS send failed");
      }
      return data as { ok: true; messageId?: string | null };
    },
  });
}
